import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <SEO title="Terms of Service" description="Terms of service and usage agreement for NinjaCopia software." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg dark:prose-invert max-w-none"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
            Terms of Service
          </h1>

          <div className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Last updated: January 2025
          </div>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                By purchasing, downloading, installing, or using NinjaCopia software ("Software"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Software.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. License Grant</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license to use the Software for your personal or business trading activities. This license allows you to:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                <li>Install and use the Software on the number of machines specified in your license</li>
                <li>Use the Software to copy trades between your NinjaTrader accounts</li>
                <li>Receive updates and support as specified in your license terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Restrictions</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                You may not:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                <li>Reverse engineer, decompile, or disassemble the Software</li>
                <li>Distribute, sell, lease, or sublicense the Software</li>
                <li>Remove or modify any copyright notices or licensing information</li>
                <li>Use the Software for any illegal or unauthorized purpose</li>
                <li>Exceed the number of machine activations specified in your license</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Trading Risks</h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-4">
                <p className="text-yellow-800 dark:text-yellow-200 font-semibold mb-2">
                  IMPORTANT TRADING RISK DISCLAIMER
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Trading involves substantial risk of loss and is not suitable for all investors. The Software is a tool to assist in trade execution but does not guarantee profits or prevent losses. You are solely responsible for your trading decisions and their consequences.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Refund Policy</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                We offer a 30-day money-back guarantee from the date of purchase. To request a refund, contact our support team within 30 days. Refunds are processed to the original payment method within 5-10 business days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Updates and Support</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Your license includes free updates for one year from the date of purchase. After this period, you may continue using your licensed version indefinitely, but major version updates may require an upgrade fee.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Disclaimer of Warranties</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO TRADING LOSSES, LOSS OF PROFITS, OR LOSS OF DATA.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Third-Party Software</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                NinjaCopia requires NinjaTrader 8 to function. NinjaTrader® is a registered trademark of NinjaTrader Group, LLC. NinjaCopia is an independent product and is not endorsed by or affiliated with NinjaTrader Group, LLC.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Contact Information</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                For questions about these Terms, please contact us at: <br />
                <span className="text-blue-600 dark:text-blue-400">support@ninjacopia.com</span>
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div >
  );
};

export default TermsPage;